// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 — see ADDITIONAL-TERMS.md.

// First-party signal processing for the waveform visualisation. Pure, dependency-free,
// and unit-tested — the browser only ever *drives* these functions with real samples.

/** Down-mix any number of channels to a single mono Float32Array (equal-weight average). */
export function mixToMono(channels: Float32Array[]): Float32Array {
  if (channels.length === 0) return new Float32Array(0);
  if (channels.length === 1) return channels[0];
  const len = channels[0].length;
  const out = new Float32Array(len);
  const n = channels.length;
  for (let c = 0; c < n; c++) {
    const ch = channels[c];
    const m = Math.min(len, ch.length);
    for (let i = 0; i < m; i++) out[i] += ch[i];
  }
  const inv = 1 / n;
  for (let i = 0; i < len; i++) out[i] *= inv;
  return out;
}

/** Smallest power of two >= n (n >= 1). */
export function nextPow2(n: number): number {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

const hannCache = new Map<number, Float32Array>();
/** Periodic Hann window of length n, memoised. */
export function hann(n: number): Float32Array {
  const cached = hannCache.get(n);
  if (cached) return cached;
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / n);
  hannCache.set(n, w);
  return w;
}

/**
 * In-place iterative radix-2 Cooley–Tukey FFT. `re`/`im` must be the same power-of-two length.
 */
export function fftRadix2(re: Float32Array, im: Float32Array): void {
  const n = re.length;
  if (n <= 1) return;
  if ((n & (n - 1)) !== 0) throw new Error('fftRadix2: length must be a power of two');

  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i];
      re[i] = re[j];
      re[j] = tr;
      const ti = im[i];
      im[i] = im[j];
      im[j] = ti;
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      const half = len >> 1;
      for (let k = 0; k < half; k++) {
        const a = i + k;
        const b = a + half;
        const xr = re[b] * cr - im[b] * ci;
        const xi = re[b] * ci + im[b] * cr;
        re[b] = re[a] - xr;
        im[b] = im[a] - xi;
        re[a] += xr;
        im[a] += xi;
        const ncr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = ncr;
      }
    }
  }
}

const FREQ_MIN = 40;
const FREQ_MAX = 16000;
const DB_FLOOR = -62;
const DB_CEIL = -6;

/**
 * Log-spaced magnitude spectrum at a point in time, normalised to 0..1 per bar.
 * `centerSample` is the sample index the frame is centred on.
 */
export function computeSpectrumBars(
  mono: Float32Array,
  sampleRate: number,
  centerSample: number,
  fftSize: number,
  barCount: number,
  sensitivity: number,
): Float32Array {
  const bars = new Float32Array(barCount);
  if (mono.length === 0 || barCount === 0) return bars;

  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);
  const win = hann(fftSize);
  const start = Math.floor(centerSample - fftSize / 2);
  for (let i = 0; i < fftSize; i++) {
    const s = start + i;
    const v = s >= 0 && s < mono.length ? mono[s] : 0;
    re[i] = v * win[i];
  }
  fftRadix2(re, im);

  const halfN = fftSize >> 1;
  const norm = 1 / (fftSize / 4);
  const nyquist = sampleRate / 2;
  const fMax = Math.min(FREQ_MAX, nyquist);
  const logMin = Math.log(FREQ_MIN);
  const logMax = Math.log(fMax);

  for (let b = 0; b < barCount; b++) {
    const f0 = Math.exp(logMin + ((logMax - logMin) * b) / barCount);
    const f1 = Math.exp(logMin + ((logMax - logMin) * (b + 1)) / barCount);
    let k0 = Math.round((f0 / nyquist) * halfN);
    let k1 = Math.round((f1 / nyquist) * halfN);
    if (k1 <= k0) k1 = k0 + 1;
    if (k0 < 1) k0 = 1;
    if (k1 > halfN) k1 = halfN;

    let peak = 0;
    for (let k = k0; k < k1; k++) {
      const m = Math.hypot(re[k], im[k]) * norm;
      if (m > peak) peak = m;
    }
    const db = 20 * Math.log10(peak + 1e-8);
    let t = (db - DB_FLOOR) / (DB_CEIL - DB_FLOOR);
    t *= sensitivity;
    bars[b] = t < 0 ? 0 : t > 1 ? 1 : t;
  }
  return bars;
}

/**
 * Peak (max-abs) amplitude envelope of the whole clip across `bucketCount` buckets,
 * normalised so the loudest bucket reaches `sensitivity` (clamped to 1). Used by the
 * `wave` (progress-waveform) style.
 */
export function computePeakEnvelope(
  mono: Float32Array,
  bucketCount: number,
  sensitivity: number,
): Float32Array {
  const out = new Float32Array(bucketCount);
  if (mono.length === 0 || bucketCount === 0) return out;
  const per = mono.length / bucketCount;
  let globalMax = 1e-6;
  for (let i = 0; i < bucketCount; i++) {
    const s0 = Math.floor(i * per);
    const s1 = Math.min(mono.length, Math.floor((i + 1) * per));
    let peak = 0;
    for (let s = s0; s < s1; s++) {
      const a = Math.abs(mono[s]);
      if (a > peak) peak = a;
    }
    out[i] = peak;
    if (peak > globalMax) globalMax = peak;
  }
  const inv = (1 / globalMax) * sensitivity;
  for (let i = 0; i < bucketCount; i++) {
    const v = out[i] * inv;
    out[i] = v > 1 ? 1 : v;
  }
  return out;
}

/**
 * Temporal smoothing: fast attack, slow release ("peak-hold" feel). Returns a new array;
 * `prev` is the previous frame's smoothed values (same length as `next`).
 */
export function smoothBars(
  prev: Float32Array,
  next: Float32Array,
  attack: number,
  release: number,
): Float32Array {
  const out = new Float32Array(next.length);
  for (let i = 0; i < next.length; i++) {
    const p = prev[i] ?? 0;
    const n = next[i];
    out[i] = n > p ? p + (n - p) * attack : p + (n - p) * release;
  }
  return out;
}
